import { DeleteOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useState } from 'react';

import {
  RecyclableWasteType,
  RecyclableWasteTypeValues,
} from '@/domain/impact-co2/entities/question.entity';
import {
  ValuesRecyclableWaste,
  VenueCo2MonthValues,
} from '@/domain/project-venue-month-declaration/entities/project-venue-month-declaration';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { thStyle } from '@/ui/components/table/Table.utils';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { QuizEdition, QuizNotEdition } from '../Co2MonthQuizComponents';
import { useMonthDeclarationDisableSubmit } from './hooks/useMonthDeclarationDisableSubmit';

export function RecyclableWasteForm({
  onEdit,
  co2MonthValues,
}: {
  onEdit: (co2Values: VenueCo2MonthValues) => void;
  co2MonthValues: VenueCo2MonthValues;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [values, setValues] = useState(
    structuredClone(co2MonthValues.wasteRecyclable),
  );

  const { disableSubmitActions } = useMonthDeclarationDisableSubmit();

  const addAnswer = (value: RecyclableWasteType) => {
    setValues([
      ...values,
      {
        recyclableWasteType: value,
        recyclableWasteQuantity: 0,
        sector: 'SITE',
      },
    ]);
  };

  const updateQuantity = (value: string, index: number) => {
    const newValues = [...values];
    newValues[index].recyclableWasteQuantity = Number(value);
    if (!isNaN(newValues[index].recyclableWasteQuantity)) {
      setValues(newValues);
    }
  };

  const removeAnswer = (index: number) => {
    const newValues = [...values];
    newValues.splice(index, 1);
    setValues(newValues);
  };

  const handleEdit = () => {
    onEdit({ ...co2MonthValues, wasteRecyclable: values });
    setIsEditing(false);
  };

  const handleRemoveClick = () => {
    setIsEditing(false);
  };

  return (
    <div>
      {isEditing ? (
        <QuizEdition
          removeFromEditing={handleRemoveClick}
          title={translate(
            'ProjectsVenue.MonthCo2Quiz.RecyclableWaste.how-much-recyclable-waste',
          )}
          form={
            <Form
              onSubmit={handleEdit}
              values={values}
              disableSubmit={disableSubmitActions}
              addAnswer={addAnswer}
              updateQuantity={updateQuantity}
              removeAnswer={removeAnswer}
            />
          }
        />
      ) : (
        <QuizNotEdition
          addToEditing={() => setIsEditing(true)}
          answeredValues={values.map((value) => {
            return `${translate(`ImpactQuestion.RECYCLABLE_WASTE.recyclableWasteTypeValues.${value.recyclableWasteType}`)} : ${value.recyclableWasteQuantity}`;
          })}
          title={translate(
            'ProjectsVenue.MonthCo2Quiz.RecyclableWaste.how-much-recyclable-waste',
          )}
          isAnswered={values.length > 0}
        />
      )}
    </div>
  );
}

function Form({
  addAnswer,
  updateQuantity,
  removeAnswer,
  disableSubmit,
  onSubmit,
  values,
}: {
  onSubmit: () => void;
  addAnswer: (value: RecyclableWasteType) => void;
  disableSubmit: boolean;
  updateQuantity: (value: string, index: number) => void;
  removeAnswer: (index: number) => void;
  values: ValuesRecyclableWaste;
}) {
  const [selected, setSelected] = useState<RecyclableWasteType | ''>('');

  const getSelectValues = () => {
    const selectedItems = values.map((value) => value.recyclableWasteType);

    return selectedItems
      ? RecyclableWasteTypeValues.filter((acc) => !selectedItems.includes(acc))
      : RecyclableWasteTypeValues;
  };

  const addType = () => {
    if (selected) {
      addAnswer(selected);
      setSelected('');
    }
  };

  const handleChangeQuantity = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    updateQuantity(e.target.value, index);
  };

  const handleSubmit = () => {
    onSubmit();
  };

  return (
    <Stack>
      <Table align="right">
        <TableHead>
          <TableRow>
            <TableCell style={thStyle}>
              {translate(
                'ImpactQuestion.RECYCLABLE_WASTE.recyclableWasteTypeTitle',
              )}
            </TableCell>
            <TableCell style={thStyle}>
              {translate('ProjectsVenue.MonthCo2Quiz.quantity')}
            </TableCell>
            <TableCell style={thStyle} />
          </TableRow>
        </TableHead>
        <TableBody>
          {values?.map((value, index) => (
            <TableRow key={'selected' + index}>
              <TableCell>
                {translate(
                  `ImpactQuestion.RECYCLABLE_WASTE.recyclableWasteTypeValues.${value.recyclableWasteType}`,
                )}
              </TableCell>
              <TableCell sx={{ py: 0 }}>
                <FSTextField
                  sx={{ width: '80px' }}
                  type="number"
                  textAlign="right"
                  value={value.recyclableWasteQuantity}
                  onChange={(e) => handleChangeQuantity(e, index)}
                />
              </TableCell>
              <TableCell sx={{ px: 0 }}>
                <Box display="flex" justifyContent="end">
                  <Button onClick={() => removeAnswer(index)}>
                    <DeleteOutline sx={{ color: ColorsRaw.basic.White }} />
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={4}>
              <Box display={'flex'} alignItems={'center'}>
                <FSTextField
                  select
                  label={translate(
                    'ImpactQuestion.RECYCLABLE_WASTE.recyclableWasteTypeTitle',
                  )}
                  value={selected}
                  onChange={(e) =>
                    setSelected(e.target.value as RecyclableWasteType)
                  }
                  sx={{ width: '100%' }}
                >
                  {getSelectValues().map((value) => (
                    <MenuItem key={value} value={value}>
                      {translate(
                        `ImpactQuestion.RECYCLABLE_WASTE.recyclableWasteTypeValues.${value}`,
                      )}
                    </MenuItem>
                  ))}
                </FSTextField>

                <FSPrimaryButton sx={{ ml: 1 }} onClick={addType}>
                  {translate('Form.add')}
                </FSPrimaryButton>
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Box marginTop={2}>
        <FSPrimaryButton
          type="submit"
          disabled={disableSubmit}
          onClick={handleSubmit}
        >
          {translate('Form.save')}
        </FSPrimaryButton>
      </Box>
    </Stack>
  );
}
